import { useFormik } from 'formik'
import ReactQuill from 'react-quill'
import * as yup from 'yup'
import { Container, Grid, TextField } from '@mui/material'
import { encode, decode } from 'html-entities'
import Dialog from 'components/dialog/dialog'
import Loading from 'components/loading/loading'
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import { useGetPrivacyPolicyDetailQuery, useUpdatePrivacyPolicyMutation } from 'store/privacy'
import type { PartialPrivacyDetailDataProps, PartialPrivacyDetailProps } from 'modules/partial'
import type { DetailStateProps, ErrorProps } from 'modules/types'
import DialogStyle from './style'

import 'react-quill/dist/quill.snow.css'

const Edit = ({ open, id, onClose }: DetailStateProps & { id: string }) => {
  if (!open) return <></>

  const { data, isFetching, isLoading } = useGetPrivacyPolicyDetailQuery({ key: id })
  const [updatePrivacyPolicy, update] = useUpdatePrivacyPolicyMutation()

  const scheme = yup.object<PartialPrivacyDetailDataProps>({
    key: yup.string().required('Key is required'),
    data: yup.array().of(
      yup.object({
        id: yup.number().required('Id is required'),
        language: yup.string().required('Language is required'),
        body: yup.string()
      })
    )
  })

  const formik = useFormik<PartialPrivacyDetailProps>({
    validationSchema: scheme,
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      key: (data && data.key) || '',
      data: [
        {
          id: (data && data.data && data.data[0] && data.data[0].id) || 0,
          language: (data && data.data && data.data[0] && data.data[0].language) || '',
          body: (data && data.data && data.data[0] && decode(data.data[0].body)) || '',
          status: (data && data.data && data.data[0] && data.data[0].status) || null
        },
        {
          id: (data && data.data && data.data[1] && data.data[1].id) || 0,
          language: (data && data.data && data.data[1] && data.data[1].language) || '',
          body: (data && data.data && data.data[1] && decode(data.data[1].body)) || '',
          status: (data && data.data && data.data[1] && data.data[1].status) || null
        }
      ]
    },
    onSubmit: (values: PartialPrivacyDetailProps) =>
      updatePrivacyPolicy({
        ...values,
        data: values.data.map((el) => ({ ...el, body: encode(el.body) }))
      })
  })

  const loading = (isLoading || isFetching) && !data

  const lang = ['EN-US', 'ID']

  const gridDataLabel = ['Language']
  const gridDataValue = ['language']

  return (
    <>
      <Dialog
        title='Update Privacy Policy'
        open={open}
        onCancel={onClose}
        onSubmit={() => formik.handleSubmit()}
        loading={update.isLoading}
        isDisabled={!formik.isValid}
      >
        <Container {...DialogStyle.Container}>
          {loading && <Loading />}
          {!loading && (
            <>
              <TextField
                name='key'
                variant='outlined'
                label='Privacy Policy Name'
                value={formik.values.key}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ readOnly: true }}
                error={formik.touched && formik.touched.key && Boolean(formik.errors.key)}
                helperText={
                  formik.touched && formik.touched.key && formik.errors && formik.errors.key
                }
                fullWidth
              />
              {lang.map((_, index) => (
                <>
                  <Grid key={index} container spacing={2}>
                    {gridDataLabel.map((title, id) => (
                      <Grid key={id} item xs={12}>
                        <TextField
                          variant='outlined'
                          label={title}
                          value={
                            (formik.values.data &&
                              formik.values.data[index][
                                gridDataValue[id] as keyof PartialPrivacyDetailDataProps
                              ]) ||
                            '-'
                          }
                          inputProps={{ readOnly: true }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <ReactQuill
                    theme='snow'
                    value={formik.values.data && formik.values.data[index].body}
                    onChange={(e) =>
                      formik.setFieldValue(`data[${index}].body`, GLOBAL.emptyQuill(e))
                    }
                  />
                </>
              ))}
            </>
          )}
        </Container>
      </Dialog>

      <Notification
        open={!update.isLoading && !update.isUninitialized}
        onClose={() => (update.isError ? update.reset() : location.reload())}
        isError={Boolean(update.error) && update.isError}
        message={GLOBAL.returnExceptionMessage(update.isError, update.error as ErrorProps)}
      />
    </>
  )
}

export default Edit
